import { MasterKey, SelectedMasterKeyService } from '@overtur-monorepo/keying-hierarchy'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { take, forkJoin, BehaviorSubject, map, Observable, throwError } from 'rxjs'
import { catchError, finalize } from 'rxjs/operators'
import { KeyingAppEnvironmentConfig } from '../configuration/keying.app.environment.config'
import { ErrorService } from './error.service'
import { MasterKeyService } from './keying.masterkey.service'
import { KeyingSystemService } from './keying.system.service'
import { LoadingService } from './loading.service'
import { TokenizedMessageLoadingService } from './tokenized-message-loading/tokenized-message-loading.service'
import { StructureDetails } from '../models/dev.test.models'
import { KeyNodeType } from '@overtur-monorepo/keying-shared'
import { MasterKeyService as LibMasterKeyService } from '@overtur-monorepo/keying-hierarchy'
import { Router } from '@angular/router'
import { AppRoutingConfig } from '../configuration/app.routing.config'
import { LoggingService } from './logging.service'

@Injectable({
  providedIn: 'root',
})
export class KeyingDataSetsService {
  keyingDataSets = new BehaviorSubject<KeyingDataSets>(null)
  keyingDataSets$ = this.keyingDataSets.asObservable()

  structureDataSets = new BehaviorSubject<StructureDataSets>(null)
  structureDataSets$ = this.structureDataSets.asObservable()

  keySystemDataSets = new BehaviorSubject<KeySystemDataSets>(null)
  keySystemDataSets$ = this.keySystemDataSets.asObservable()

  combinatingRuleSets = new BehaviorSubject<CombinatingRuleSets>(null)
  combinatingRuleSets$ = this.combinatingRuleSets.asObservable()

  structureDetails = new BehaviorSubject<StructureDetails>(null)
  structureDetails$ = this.structureDetails.asObservable()

  actionType = new BehaviorSubject<string>(null)
  actionType$ = this.actionType.asObservable()

  constructor(
    @Inject(HttpClient) private httpClient: HttpClient,
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig,
    private errorService: ErrorService,
    private loadingService: LoadingService,
    private tokenizedMessageLoadingService: TokenizedMessageLoadingService,
    private masterKeyService: MasterKeyService,
    private keyingSystemService: KeyingSystemService,
    private libMasterKeyService: LibMasterKeyService,
    private selectedMasterKeyService: SelectedMasterKeyService,
    private router: Router,
    private loggingService: LoggingService
  ) {}

  getReservationTypes() {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/ReservationTypes`
    return this.httpClient.get<any[]>(url)
  }

  getCombinatingRuleSets() {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/CombinatingRuleSets`
    return this.httpClient.get<any[]>(url)
  }

  getConstructionTypes() {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/ConstructionTypes`
    return this.httpClient.get<any[]>(url)
  }

  getChamberTypes() {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/ChamberTypes`
    return this.httpClient.get<any[]>(url)
  }

  getCountries() {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Countries`
    const collator = new Intl.Collator()

    return this.httpClient.get<any[]>(url).pipe(
      map((countries) => {
        countries = countries.sort((a, b) => {
          if (a.code === 'US') {
            return -1
          }
          return collator.compare(a.name, b.name)
        })
        countries.forEach((c) => (c.states = c.states.sort((a, b) => collator.compare(a.name, b.name))))
        return countries
      })
    )
  }

  getKeywayFamilies() {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/KeywayFamilies`
    return this.httpClient.get<any[]>(url)
  }

  getStructureDetails(structureId): Observable<StructureDetails> {
    const token = this.tokenizedMessageLoadingService.startLoading(
      'Loading Structure Details',
      `get-structure-details-${structureId}-${Date.now()}`,
      'medium'
    )

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/details`

    return this.httpClient.get<StructureDetails>(url).pipe(
      catchError((error) => {
        this.errorService.throw(error)
        return throwError(() => error)
      }),
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }

  getKeyways(keywayFamilyId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/KeywayFamilies/${keywayFamilyId}/keyways`
    return this.httpClient.get<any[]>(url)
  }

  getKeyway(keywayFamilyId, keywayId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/KeywayFamilies/${keywayFamilyId}/keyways/${keywayId}`
    return this.httpClient.get<any[]>(url)
  }

  getParentKeyWays(keywayFamilyId, keywayId) {
    const headers = new HttpHeaders().set('Content-type', 'application/allegion.keying.get.parent.keyways+json')

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/KeywayFamilies/${keywayFamilyId}/keyways/${keywayId}`
    return this.httpClient.get<any>(url, { headers })
  }

  getKeywayCombinatingRuleSets(keywayFamilyId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/KeywayFamilies/${keywayFamilyId}/CombinatingRuleSets`
    return this.httpClient.get<any[]>(url)
  }

  getKeywayCombinatingRules(keywayFamilyId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/CombinatingRuleSets/KeywayFamily/${keywayFamilyId}`
    return this.httpClient.get<any[]>(url)
  }

  setKeyingDataSets(keyingDataSets: KeyingDataSets) {
    this.keyingDataSets.next(keyingDataSets)
  }

  setKeySystemDataSets(keySystemDataSets: KeySystemDataSets) {
    this.keySystemDataSets.next(keySystemDataSets)
  }

  loadKeyingDataSets() {
    const token = this.tokenizedMessageLoadingService.startLoading('Getting Keying Data', 'keying-data-sets')
    forkJoin([
      this.getCountries(),
      this.getCombinatingRuleSets(),
      this.getConstructionTypes(),
      this.getKeywayFamilies(),
      this.getReservationTypes(),
      this.getChamberTypes(),
    ])
      .pipe(take(1))
      .subscribe(
        (resData) => {
          const newData = new KeyingDataSets()
          newData.countries = resData[0]
          newData.combinatingRuleSets = resData[1]
          newData.constructionTypes = resData[2]
          newData.keywayFamilies = resData[3]
          newData.reservationTypes = resData[4]
          newData.chamberTypes = resData[5]

          this.loggingService.log('Keying Data Loaded:', newData)

          this.keyingDataSets.next(newData)

          this.tokenizedMessageLoadingService.stopLoading(token)
        },
        (error) => {
          this.tokenizedMessageLoadingService.stopLoading(token)
          this.errorService.throw(error)
        }
      )
  }

  loadKeySystemDataSets(
    structureId,
    keySystemId,
    isCrossKeyAdded = false,
    newChildName = '',
    RouteToHierarchy = false
  ) {
    this.loadingService.set({ isLoading: true, message: 'Getting Key System Data', isSilent: true })
    forkJoin([
      this.masterKeyService.getMasterKeys(structureId, keySystemId, true),
      this.masterKeyService.getHierarchyMasterKeys(structureId, keySystemId),
      this.keyingSystemService.getKeySystem(structureId, keySystemId),
    ])
      .pipe(take(1))
      .subscribe(
        (resData) => {
          const newData = new KeySystemDataSets()
          newData.masterKeysBudget = resData[0]
          newData.masterKeysKeying = resData[1]
          // newData.masterKeysKeying = this.formatItem(resData[2])
          newData.keySystem = resData[2]
          // newData.hierarchyKeys = resData[3]

          newData.keySystem.masterKeysBudget = newData.masterKeysBudget
          newData.keySystem.masterKeysKeying = newData.masterKeysKeying
          this.keyingSystemService.selectKeySystem(newData.keySystem)

          this.loggingService.log('Key System Data Loaded:', newData)

          this.keySystemDataSets.next(newData)

          if (isCrossKeyAdded && newChildName) {
            const crossKeyMaster = newData.masterKeysKeying.children.find(
              (m) => m.masterKeyTypeId === KeyNodeType.CrossKeyMaster
            )
            if (crossKeyMaster) {
              const addedCrossKey = crossKeyMaster.children.find(
                (m) => m.keySetSymbol.toLowerCase() === newChildName.toLowerCase()
              )
              if (addedCrossKey) {
                this.libMasterKeyService.setSelectedMasterKey(addedCrossKey)
                this.selectedMasterKeyService.setSelectedMasterKey(addedCrossKey)
              }
            }
          }

          this.loadingService.set({ isLoading: false, message: '' })

          if (RouteToHierarchy) {
            this.router.navigate([AppRoutingConfig.getStructureHierarchyUrl(structureId, keySystemId)])
          }
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  setKeySectionName(structure, keySystem) {
    this.getKeyway(structure.keywayFamilyId, keySystem.keySectionId)
      .pipe(take(1))
      .subscribe((keyway) => {
        keySystem.keySectionName = keyway['name']
        return keySystem
      })
  }

  // private formatItem(hierarchyItem: MasterKey): MasterKey {
  //   hierarchyItem = Object.assign(new MasterKey(), hierarchyItem)

  //   if (hierarchyItem.children && hierarchyItem.children.length > 0) {
  //     for (let i = 0; i < hierarchyItem.children.length; i++) {
  //       hierarchyItem.children[i] = this.formatItem(hierarchyItem.children[i])
  //     }
  //   }
  //   return hierarchyItem
  // }

  loadStructureDataSets(structure) {
    this.loadingService.set({ isLoading: true, message: 'Getting Structure Data', isSilent: false })
    forkJoin([this.getKeyways(structure.keywayFamilyId)])
      .pipe(take(1))
      .subscribe(
        (resData) => {
          const newData = new StructureDataSets()
          newData.keyways = resData[0]

          this.loggingService.log('Structure Data Loaded:', newData)

          this.structureDataSets.next(newData)

          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  loadCombinatingRuleSets(keywayFamilyId) {
    this.loadingService.set({ isLoading: true, message: 'Getting Combinating Rule Sets', isSilent: false })
    forkJoin([this.getKeywayCombinatingRuleSets(keywayFamilyId), this.getKeywayCombinatingRules(keywayFamilyId)])
      .pipe(take(1))
      .subscribe(
        (resData) => {
          const newData = new CombinatingRuleSets()
          newData.combinatingRuleSets = resData[0]
          newData.combinatingRules = resData[1]

          this.loggingService.log('Combinating Rule Sets Loaded:', newData)

          this.combinatingRuleSets.next(newData)

          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  loadStructureDetails(structureId, actionType: string = null) {
    // Loading is handled by getStructureDetails via TokenizedMessageLoadingService
    this.getStructureDetails(structureId)
      .pipe(take(1))
      .subscribe(
        (details) => {
          this.actionType.next(actionType)
          this.structureDetails.next(details)
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }
}

export class KeyingDataSets {
  combinatingRuleSets = []
  constructionTypes = []
  countries = []
  keywayFamilies = []
  chamberTypes = []
  reservationTypes = []
}

export class StructureDataSets {
  keyways = []
  tmk
}

export class KeySystemDataSets {
  masterKeysBudget: MasterKey
  masterKeysKeying: MasterKey
  notes = []
  keySystem
  hierarchyKeys: MasterKey
}

export class CombinatingRuleSets {
  combinatingRules
  combinatingRuleSets
}
